import { spErrorTags } from '@eventr/common'
import { useIpInfo } from '@eventr/common-storybook'
import { signal } from '@preact/signals-react'
import { LngLat, StyleSpecification } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useEffect, useMemo } from 'react'
import { Map, MapRef } from 'react-map-gl/maplibre'
import { useShallow } from 'zustand/react/shallow'
import { isConnectivityAvailable } from '../../../feat/connectivity/connectivityListener'
import { useBoundStore } from '../../../state/bound.store'
import { useLiveLocationStore } from '../../../state/liveLocationState/liveLocation.store'
import { useThemeContext } from '../../../state/themeState/themeContext'
import { useEphemeralStore } from '../../../state/useEphemeralStore'
import MapDrawer from './components/mapDrawer'
import MapToolbar from './components/mapToolbar'
import NoConnectionMapsPage from './components/noConnectionMapsPage'
import FullScreenItineraryTitle from './controls/fullScreenItineraryTitle'
import LiveLocationShareControl from './controls/liveLocationShareControl'
import RecentreMapControl from './controls/recentreMapControl'
import ToggleFullscreenMapControl from './controls/toggleFullscreenMapControl'
import './css/hide-attribution.css'
import calculateBounds from './functions/calculateBounds'
import { extractLngLatLabelledItineraryComponents } from './functions/extractLngLatLabelledItineraryComponents'
import getMapStyle from './functions/getMapStyleURL'
import ItineraryComponentMarkers from './markers/itineraryComponentsMarkers'
import { ItineraryGeneralInfoMarker } from './markers/itineraryGeneralInfoMarker'
import TeamMemberLocationMarkers from './markers/teamMemberLocationMarkers'
import UserLocationMarker from './markers/userLocationMarker'

export const mapRefSignal = signal<MapRef>(null)

/**
 * The map component.
 *
 * Read the specific documentation [here](./README.md).
 * @returns The map component.
 */
export default function ItineraryMap() {
    // MARK: EXTERNAL STATE

    // Get the itinerary components and map view state (setter and getter) from the store.
    const { itineraryComponents, getEventInfoV2, selectedItinID } =
        useBoundStore(
            useShallow((s) => ({
                getEventInfoV2: s.getEventInfoV2,
                itineraryComponents: s.itineraryComponents,
                selectedItinID: s.selectedItinID,
            }))
        )

    const liveLocations = useLiveLocationStore(
        useShallow((s) => s.liveLocations)
    )
    const userPosition = useLiveLocationStore(useShallow((s) => s.userPosition))

    const {
        mapViewState,
        setMapViewState,
        includeLiveLocationsInBounds,
        mapIsFullscreen,
        selectedLngLatLabelledItineraryComponents,
        mapFlyTo,
    } = useEphemeralStore(
        useShallow((s) => {
            return {
                mapViewState: s.mapViewState,
                setMapViewState: s.setMapViewState,
                includeLiveLocationsInBounds: s.includeLiveLocationsInBounds,
                mapIsFullscreen: s.mapIsFullscreen,
                selectedLngLatLabelledItineraryComponents:
                    s.selectedLngLatLabelledItineraryComponents,
                mapFlyTo: s.mapFlyTo,
            }
        })
    )

    // Get the selected colour scheme from the theme context
    const selectedColourScheme = useThemeContext(
        useShallow((s) => s.selectedColourScheme)
    )

    const isGeneralInfoMarker: false | LngLat = useMemo(() => {
        const generalInfoV2 = getEventInfoV2()
        if (
            generalInfoV2 &&
            generalInfoV2.data &&
            generalInfoV2.data.OSMAddress &&
            generalInfoV2.data.OSMAddress.lat &&
            generalInfoV2.data.OSMAddress.lon
        ) {
            return new LngLat(
                generalInfoV2.data.OSMAddress.lon,
                generalInfoV2.data.OSMAddress.lat
            )
        } else {
            return false
        }
    }, [selectedItinID])

    // MARK: INTERNAL STATE

    // Extract the locations of the itinerary components.

    // N.B. This is done here oppose to `ItineraryComponentMarkers` so bounds can be calculated.
    const lngLatLabelledItineraryComponents = useMemo(
        () => extractLngLatLabelledItineraryComponents(itineraryComponents),
        [itineraryComponents]
    )

    // MARK: COMPUTED PROPERTIES

    function recalculateBounds() {
        const bounds = calculateBounds(
            lngLatLabelledItineraryComponents.map((l) => l.lngLat)
        )

        if (isGeneralInfoMarker) {
            bounds.extend(isGeneralInfoMarker)
        }

        if (includeLiveLocationsInBounds) {
            liveLocations.forEach((liveLocation) => {
                const lngLat = new LngLat(
                    liveLocation.lngLat.lng,
                    liveLocation.lngLat.lat
                )

                bounds.extend(lngLat)
            })

            if (userPosition && userPosition.coords) {
                const lngLat = new LngLat(
                    userPosition.coords.longitude,
                    userPosition.coords.latitude
                )

                bounds.extend(lngLat)
            }
        }

        if (bounds.isEmpty()) {
            const ipInfo = useIpInfo.getState().ipInfo
            if (ipInfo) {
                bounds.extend(new LngLat(ipInfo.longitude, ipInfo.latitude))
            }

            mapFlyTo(bounds.getCenter(), false, bounds, 5)
        } else if (!selectedLngLatLabelledItineraryComponents) {
            mapFlyTo(bounds.getCenter(), false, bounds)
        }
    }

    useEffect(() => {
        recalculateBounds()
    }, [
        lngLatLabelledItineraryComponents,
        includeLiveLocationsInBounds,
        isGeneralInfoMarker,
        selectedLngLatLabelledItineraryComponents,
    ])

    // Get the map style URL based on the selected colour scheme.
    const mapStyle: StyleSpecification = useMemo(
        () => getMapStyle(selectedColourScheme),
        [selectedColourScheme]
    )

    // Show the no connection page if there is no internet connection.
    if (!isConnectivityAvailable.value) return <NoConnectionMapsPage />

    return (
        <div
            style={{
                position: 'relative',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <Map
                {...mapViewState}
                onMove={(e) => setMapViewState(e.viewState)}
                ref={(node) => {
                    mapRefSignal.value = node
                }}
                mapStyle={mapStyle}
                onLoad={recalculateBounds}
                reuseMaps
                maxZoom={19}
                minZoom={0}
                onTouchEnd={(event) => {
                    if (!event.target.isMoving()) {
                        /// I guess add a activity
                        /// This is a to do. ignore for now.
                        console.log('Add Activity???')
                    }
                }}
                onError={(error) => {
                    spErrorTags(error, ['ERROR', 'Map'])
                }}
            >
                <ItineraryGeneralInfoMarker />

                <ItineraryComponentMarkers
                    locations={lngLatLabelledItineraryComponents}
                />

                <UserLocationMarker />

                <TeamMemberLocationMarkers />
            </Map>

            <MapToolbar position="top-left">
                <ToggleFullscreenMapControl />
            </MapToolbar>

            <MapToolbar position="bottom-right">
                <RecentreMapControl />
            </MapToolbar>

            <MapToolbar position="bottom-left">
                <LiveLocationShareControl bottomFab />
            </MapToolbar>

            {mapIsFullscreen && <FullScreenItineraryTitle />}
            <MapDrawer />
        </div>
    )
}
